// Pure, testable analysis logic for the Family Snapshot tool.
// No I/O, no React. Safe on server and client.

export type Dependent = { age: number };

export type SnapshotInput = {
  household: {
    your_age: number;
    spouse_age?: number | null;
    dependents: Dependent[];
    others_dependent_note?: string;
  };
  income: {
    your_income: number;
    spouse_income?: number | null;
    months_savings: number; // 0..12 (12 means 12+)
    job_security_rating: number; // 1..5
  };
  coverage: {
    life_self: number;
    life_spouse: number;
    has_disability: "yes" | "no" | "unsure";
    has_critical_illness: "yes" | "no" | "unsure";
    estate_docs: string[]; // any of WILL,TRUST,POA,HEALTHCARE,BENEFICIARIES
  };
  goals: string[];
};

export type GapResult = {
  you: { recommended: number; current: number; gap: number };
  spouse: { recommended: number; current: number; gap: number } | null;
  totalGap: number;
};

export type Recommendation = {
  id: string;
  priority: "high" | "medium" | "low";
  title: string;
  description: string;
  cta_label: string;
  cta_href: string;
};

export type SnapshotAnalysis = {
  gap: GapResult;
  protection_score: number;
  band: "strong" | "meaningful" | "significant" | "foundational";
  band_message: string;
  recommendations: Recommendation[];
};

const MORTGAGE_ESTIMATE = 250_000; // assumed avg outstanding mortgage
const EDUCATION_PER_DEPENDENT = 100_000;
const DEBT_ESTIMATE = 25_000; // non-mortgage consumer debt buffer

/** DIME need per earner: Debt + Income*10 + Mortgage + Education*deps */
export function dimeNeed(income: number, dependentCount: number): number {
  if (income <= 0) return 0;
  return (
    DEBT_ESTIMATE +
    income * 10 +
    MORTGAGE_ESTIMATE +
    EDUCATION_PER_DEPENDENT * dependentCount
  );
}

export function computeGap(input: SnapshotInput): GapResult {
  const deps = input.household.dependents.length;
  const youNeed = dimeNeed(input.income.your_income, deps);
  const youCurrent = Math.max(0, input.coverage.life_self || 0);
  const you = {
    recommended: youNeed,
    current: youCurrent,
    gap: Math.max(0, youNeed - youCurrent),
  };

  let spouse: GapResult["spouse"] = null;
  if (input.household.spouse_age && (input.income.spouse_income ?? 0) > 0) {
    const sNeed = dimeNeed(input.income.spouse_income || 0, deps);
    const sCurrent = Math.max(0, input.coverage.life_spouse || 0);
    spouse = {
      recommended: sNeed,
      current: sCurrent,
      gap: Math.max(0, sNeed - sCurrent),
    };
  }

  return {
    you,
    spouse,
    totalGap: you.gap + (spouse?.gap ?? 0),
  };
}

/**
 * Protection Score (0-100), weighted:
 * - Coverage adequacy (40 pts): % of recommended currently in place (avg across spouses)
 * - Disability (15 pts): yes=15, unsure=7, no=0
 * - Critical illness / living benefits (10 pts): yes=10, unsure=5, no=0
 * - Estate docs (25 pts): 5 pts per doc up to 5
 * - Emergency savings (10 pts): months_savings / 12 * 10 (capped)
 */
export function protectionScore(input: SnapshotInput, gap: GapResult): number {
  const adequacyYou =
    gap.you.recommended > 0
      ? Math.min(1, gap.you.current / gap.you.recommended)
      : 1;
  const adequacySpouse = gap.spouse
    ? gap.spouse.recommended > 0
      ? Math.min(1, gap.spouse.current / gap.spouse.recommended)
      : 1
    : null;
  const adequacy =
    adequacySpouse === null ? adequacyYou : (adequacyYou + adequacySpouse) / 2;
  const coveragePts = adequacy * 40;

  const disabilityPts =
    input.coverage.has_disability === "yes"
      ? 15
      : input.coverage.has_disability === "unsure"
        ? 7
        : 0;

  const criticalPts =
    input.coverage.has_critical_illness === "yes"
      ? 10
      : input.coverage.has_critical_illness === "unsure"
        ? 5
        : 0;

  const docCount = Math.min(5, input.coverage.estate_docs.length);
  const estatePts = docCount * 5;

  const months = Math.min(12, Math.max(0, input.income.months_savings));
  const savingsPts = (months / 12) * 10;

  return Math.round(coveragePts + disabilityPts + criticalPts + estatePts + savingsPts);
}

export function scoreBand(score: number): SnapshotAnalysis["band"] {
  if (score >= 80) return "strong";
  if (score >= 60) return "meaningful";
  if (score >= 40) return "significant";
  return "foundational";
}

export function bandMessage(band: SnapshotAnalysis["band"]): string {
  switch (band) {
    case "strong":
      return "Strong foundation. A few targeted refinements will keep your plan ahead of life's changes.";
    case "meaningful":
      return "Meaningful gaps. The core is in place — closing a few specific items will materially strengthen your family's position.";
    case "significant":
      return "Significant attention needed. Key protections are missing; addressing them is the highest-leverage step you can take this quarter.";
    case "foundational":
      return "Foundational work recommended. We'd start with one or two essentials before anything else — it's the right order.";
  }
}

const ESTATE_DOCS_REQUIRED = ["WILL", "BENEFICIARIES"] as const;

export function buildRecommendations(
  input: SnapshotInput,
  gap: GapResult,
): Recommendation[] {
  const recs: Recommendation[] = [];

  const sigGap = (gap.you.gap > 250_000) || (gap.spouse?.gap ?? 0) > 250_000;
  if (sigGap) {
    recs.push({
      id: "life-gap",
      priority: "high",
      title: "Significant life insurance gap",
      description:
        "Your current life coverage falls short of the income, debt, and education your family depends on. Closing this gap is the single largest protection lever you have.",
      cta_label: "Review coverage options",
      cta_href: "/solutions",
    });
  }

  if (input.coverage.has_disability !== "yes") {
    recs.push({
      id: "disability",
      priority: "high",
      title: "Income protection review",
      description:
        "A disability is statistically more likely than an early death during your working years. Without it, the paycheck stops but the bills don't.",
      cta_label: "Talk through disability coverage",
      cta_href: "/book",
    });
  }

  if (!input.coverage.estate_docs.includes("WILL")) {
    recs.push({
      id: "will",
      priority: "high",
      title: "Foundational estate documents",
      description:
        "Without a will, your state decides how assets are distributed and — more importantly — who raises minor children. This is fixable in weeks, not years.",
      cta_label: "Get the estate checklist",
      cta_href: "/academy",
    });
  }

  const impliedAssets =
    (input.income.your_income + (input.income.spouse_income ?? 0)) * 10;
  if (!input.coverage.estate_docs.includes("TRUST") && impliedAssets > 1_000_000) {
    recs.push({
      id: "trust",
      priority: "medium",
      title: "Trust strategy conversation",
      description:
        "At your projected net worth, a trust can protect assets from probate, control distribution to heirs, and reduce tax friction. Worth a 30-minute conversation.",
      cta_label: "Explore trust strategy",
      cta_href: "/book",
    });
  }

  if (!input.coverage.estate_docs.includes("BENEFICIARIES")) {
    recs.push({
      id: "beneficiaries",
      priority: "medium",
      title: "Annual beneficiary audit",
      description:
        "Beneficiary designations override your will. We'll help you confirm every policy, retirement account, and bank account names the right people.",
      cta_label: "Run a beneficiary audit",
      cta_href: "/book",
    });
  }

  if (input.coverage.has_critical_illness !== "yes") {
    recs.push({
      id: "critical-illness",
      priority: "medium",
      title: "Living benefits review",
      description:
        "Modern life policies can pay you while you're alive in the event of a heart attack, stroke, or cancer diagnosis. Most older policies don't have this.",
      cta_label: "See living benefits options",
      cta_href: "/solutions",
    });
  }

  // Always include the strategist review at the bottom
  recs.push({
    id: "strategist-review",
    priority: "low",
    title: "Schedule a 30-min review with a Currency Thoughts Strategist",
    description:
      "A Strategist will walk through your snapshot, answer questions, and outline a path forward. No pitch — an educational conversation.",
    cta_label: "Book a review",
    cta_href: "/book",
  });

  // Sort: high → medium → low, preserve insertion order within
  const order = { high: 0, medium: 1, low: 2 } as const;
  return recs.sort((a, b) => order[a.priority] - order[b.priority]);
}

export function analyzeSnapshot(input: SnapshotInput): SnapshotAnalysis {
  const gap = computeGap(input);
  const protection_score = protectionScore(input, gap);
  const band = scoreBand(protection_score);
  return {
    gap,
    protection_score,
    band,
    band_message: bandMessage(band),
    recommendations: buildRecommendations(input, gap),
  };
}

export function formatUSD(n: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(Math.round(n));
}

export const ESTATE_DOC_OPTIONS = [
  { value: "WILL", label: "Will" },
  { value: "TRUST", label: "Trust" },
  { value: "POA", label: "Power of Attorney" },
  { value: "HEALTHCARE", label: "Healthcare Directive" },
  { value: "BENEFICIARIES", label: "Updated Beneficiaries" },
] as const;

export const GOAL_OPTIONS = [
  { value: "income_replacement", label: "Replace income on death" },
  { value: "pay_off_mortgage", label: "Pay off mortgage" },
  { value: "fund_education", label: "Fund education" },
  { value: "retirement_income_spouse", label: "Retirement income for spouse" },
  { value: "leave_legacy", label: "Leave a legacy" },
  { value: "final_expenses", label: "Cover final expenses" },
  { value: "business_buyout", label: "Business buyout" },
  { value: "ltc_planning", label: "Long-term care planning" },
] as const;

// Re-export the required constant so future estate-doc checks reference one source.
export { ESTATE_DOCS_REQUIRED };